import { keyframes, css } from 'styled-components'
import { faBullseye, faBellSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { Logger } from 'logger';
import React, { useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { colors, styled, themes } from '../../../assets/theme';
import { DataContainer, NormalText } from '../../../assets/theme/StyledComponents';

import { ThemeConsumer } from 'assets/theme/ThemeContext';
import { IStmomoData } from 'components/ModelTables/TrendModel';
import NumberFormat from 'react-number-format';
import { subscribeListener, unsubscribeListener } from 'redux/ably/sagas';
import { voiceOptions } from '../../../assets/sounds/widgetAudio';
import { IWatchboxData } from '../WatchBoxItem';

import audioFile from '../../../assets/sounds/alert1.mp3';
import { usePrevious } from 'hooks/usePrevious';

export interface IQuote {
  p?: number,
  nc?: number,
  yld?: number
}

export interface ITrendPrediction {
  t: string
  v: number
  model: string
  sym: string
}

interface ICompactViewProps {
  id: number,
  watchboxData?: IWatchboxData,
  symbol: string,
  instrument: string
  viewChangeCallback: (viewName: 'compact' | 'expanded' | 'stmomo') => void
  audioIsMuted: boolean
}

// const debugLog = Logger.create('CompactView.tsx')

export const CompactView: React.FunctionComponent<ICompactViewProps> = ({ id, watchboxData: data, symbol, instrument, viewChangeCallback, audioIsMuted }) => {
  const [quote, setQuote] = useState<IQuote>();
  const [activeRally, setActiveRally] = useState('');
  const [stmomoData, setStmomoData] = useState<IStmomoData>()
  const [lastTradeData, setLastTradeData] = useState<any>({})
  const [AITrendColor, setAITrendColor] = useState<string>('')
  const [trendTradeAudio, setTrendTradeAudio] = useState<HTMLAudioElement>();
  const [AITrendAudio, setAITrendAudio] = useState<HTMLAudioElement>();
  const [trendTradeSignal, setTrendTradeSignal] = useState<boolean>(false)
  const [AITrendSignal, setAITrendSignal] = useState<boolean>(false)
  const [justinTrend, setJustinTrend] = useState<ITrendPrediction>()
  const [justinBuyValue, setJustinBuyValue] = useState<string>('')
  const [stopoutVisibility, setStopoutVisibility] = useState<boolean>(false)
  const TrendTradeColor = useRef<string>('');
  const prevStmomo = usePrevious(stmomoData) as any
  const prevJustin = usePrevious(justinTrend) as any

  useEffect(() => {
    if (voiceOptions[instrument]) {
      return () => {
        voiceOptions[instrument].sell.pause();
        voiceOptions[instrument].buy.pause();
      };
    }

    return;
  }, [instrument]);

  useEffect(() => {
    if (!trendTradeAudio) return;

    if (audioIsMuted && trendTradeSignal) {
      trendTradeAudio.pause();
    } else if (!audioIsMuted && trendTradeSignal) {
      trendTradeAudio.play();
      trendTradeAudio.loop = true;
    } else {
      trendTradeAudio.pause();
      trendTradeAudio.loop = false;
    }
  }, [audioIsMuted, trendTradeSignal, trendTradeAudio]);

  useEffect(() => {
    if (!AITrendAudio) return;

    if (audioIsMuted && AITrendSignal) {
      AITrendAudio.pause();
    } else if (!audioIsMuted && AITrendSignal) {
      AITrendAudio.play();
      AITrendAudio.loop = true;
    } else {
      AITrendAudio.pause();
      AITrendAudio.loop = false;
    }
  }, [audioIsMuted, AITrendSignal, AITrendAudio]);

  useEffect(() => {
    if (stmomoData && prevStmomo) {
      const { stmomo: prevSignal, type_id: prevType } = prevStmomo;
      const { stmomo: currentSignal, type_id: currentType } = stmomoData;

      if (prevSignal !== currentSignal && prevType === currentType) {
        if (trendTradeSignal) return;

        const audioAlert = new Audio(audioFile);

        setTrendTradeAudio(audioAlert)
        setTrendTradeSignal(true)
        setStopoutVisibility(false)
      } else if (prevType !== currentType) {
        setStopoutVisibility(currentSignal === "STOPOUT");
        setTrendTradeSignal(false);
        setAITrendSignal(false)
      } else {
        setStopoutVisibility(currentSignal === "STOPOUT" && !trendTradeSignal);
      }
    }
  }, [stmomoData, prevStmomo, trendTradeSignal])

  useEffect(() => {
    if (justinTrend && prevJustin) {
      if (!justinTrend.v) return;

      setAITrendColor(
        justinTrend.v >= 0.5
          ? colors.accents.good.base
          : colors.accents.bad.base
      );
      setJustinBuyValue(justinTrend.v >= 0.5 ? "BUY" : "SELL");

      if (!isEqual(justinTrend, prevJustin) && justinTrend.sym === prevJustin.sym) {
        if (
          (justinBuyValue === 'BUY' && justinTrend.v < 0.50) ||
          (justinBuyValue === 'SELL' && justinTrend.v >= 0.50)
        ) {
          if (AITrendSignal) return;

          const audioAlert = new Audio(audioFile);

          setAITrendAudio(audioAlert);
          setAITrendSignal(true);
        }
      }
    }
  }, [justinTrend, prevJustin, justinBuyValue, audioIsMuted, AITrendSignal])

  useEffect(() => {
    subscribeListener(`sym:${symbol}:tick`, id, ({ data }) => {
      setQuote(data);
    });

    subscribeListener(`stmomo:VPM${instrument}`, id, ({ data }) => {
      setStmomoData(data)
    })

    subscribeListener(`lasttrade:VPM${instrument}`, id, ({ data }) => {
      setLastTradeData(data)
    })

    if (symbol === 'ES') {
      subscribeListener(`model:risk-reward:ensemble`, id, ({ data }) => {
        setJustinTrend(data)
      })
    }
    return () => {
      unsubscribeListener(`sym:${symbol}:tick`, id);
      unsubscribeListener(`stmomo:VPM${instrument}`, id)
      unsubscribeListener(`lasttrade:VPM${instrument}`, id)

      if (symbol === 'ES') {
        unsubscribeListener(`model:risk-reward:ensemble`, id)
      }
    }
  }, [id, symbol, instrument]);

  useEffect(() => {
    if (data) {
      setActiveRally(data.active_name || '');
    }
  }, [instrument, activeRally, data]);

  const getTradeStartDateTime = (format: string) => {
    if (!lastTradeData || !lastTradeData.time) { return '' };

    const period = parseInt(instrument.replace( /^\D+/g, ''), 10);
    const tradestart = new Date(lastTradeData.time * 1000)

    switch (period) {
      case 1:
        tradestart.setTime(tradestart.getTime() + (60 * 1000))
        break
      case 5:
        tradestart.setTime(tradestart.getTime() + (5* 60 * 1000))
        break
      case 10:
        tradestart.setTime(tradestart.getTime() + (60 * 60 * 1000))
        break
    }

    return format === 'Time' ? tradestart.toLocaleTimeString() : tradestart.toLocaleDateString()
  }

  if (!quote || !data) { return <div />; }
  const {
    p, nc,
  } = quote;
  if (p === undefined || nc === undefined) { return <div />; }
  const [indicator, color] = nc > 0 ? ['+', colors.accents.good.base] : ['', colors.accents.bad.base];
  const {
    active_name,
    active_price,
    active_tgt,
    buy_p_price,
    buy_p_tgt,
    sell_p_price,
    sell_p_tgt,
    stopout_price
} = data;

  const getTrend = () => {
    if (!stmomoData) { return <div /> }

    switch (stmomoData.stmomo) {
      case 'SELL':
        TrendTradeColor.current = colors.accents.bad.base;
        break;
      case 'BUY':
        TrendTradeColor.current = colors.accents.good.base;
        break;
      case 'STOPOUT':
        TrendTradeColor.current = colors.accents.aware.base;
        break;
      default:
        return <div />
    }
    return (
      <React.Fragment>
        <WatchBoxLabeledPair color={TrendTradeColor.current}>
          <div style={{color: TrendTradeColor.current}}>{stmomoData.stmomo}</div>
          <div>
            {
              stmomoData.initial_entry_point === undefined || stmomoData.initial_entry_point === -1 ? '': stmomoData.initial_entry_point.toFixed(2)
            }
          </div>
        </WatchBoxLabeledPair>
      </React.Fragment>
    )
  }

  const handleHistoryClick = () => {
    viewChangeCallback('stmomo')
  }

  const getWatchBoxRallyItems = () => {
    if (active_name) {
      const activeColor = active_name === 'SELL RALLY' ? colors.accents.bad.base : colors.accents.good.base
      return (
        <React.Fragment>
          <WatchBoxRallyItem style={{ fontSize: '1.2rem', backgroundColor: activeColor, padding: '0px 9px' }}>
            <NormalText style={{ fontSize: '1.1rem' }}>{active_name}</NormalText>
            {/* <NormalText>
              <FontAwesomeIcon size="1x" icon={faStar} title="Star" />
            </NormalText> */}
            <NormalText style={{ fontSize: '2em'}}>{ active_name === 'SELL RALLY' ? '>' : '<' }</NormalText>
            <NormalText style={{ fontSize: '1.1rem' }}>
              <NumberFormat
                value={active_price}
                displayType="text"
                thousandSeparator={true}
                fixedDecimalScale={true}
                decimalScale={2}
              />
            </NormalText>
          </WatchBoxRallyItem>
          <WatchBoxRallyItem>
            <NormalText style={{ fontSize: '1.1rem' }}>Target</NormalText>
            {/* <NormalText>
              <FontAwesomeIcon size="1x" icon={faStar} title="Star" />
            </NormalText> */}
            <NormalText>
              <FontAwesomeIcon size="1x" icon={faBullseye} title="Bullseye" />
            </NormalText>
            <NormalText style={{ fontSize: '1.1rem' }}>
              <NumberFormat
                  value={active_tgt}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
            </NormalText>
          </WatchBoxRallyItem>
          <WatchBoxRallyItem>
            <NormalText style={{ fontSize: '1.1rem' }}>Stop out</NormalText>
            {/* <NormalText /> */}
            <NormalText>Exit</NormalText>
            <NormalText style={{ fontSize: '1.1rem' }}>
              <NumberFormat
                  value={stopout_price}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
            </NormalText>
          </WatchBoxRallyItem>
        </React.Fragment>
      );
    }

    return (
      <WatchBoxRallyItem>
        <div style={{ gridColumn: 'span 4', textAlign: 'center', height: '112px', display: 'grid', justifyContent: 'center', alignContent: 'center' }}>
          <NormalText style={{ fontSize: '1.1em'}}>No Active</NormalText>
          <NormalText style={{ fontSize: '1.1em'}}>Mean Reversion Trade</NormalText>
        </div>
      </WatchBoxRallyItem>
    );
  };

  const handleBellClick = () => {
    if (stmomoData && stmomoData.stmomo === 'STOPOUT') setStopoutVisibility(true);

    setTrendTradeSignal(false)
  }

  return (
    <ThemeConsumer>
      {({ themeName }) => {
        if (!themeName) {
          return (<div />);
        }



        return (
        <React.Fragment>
          <WatchBoxPricing>
            <LargePriceLabel style={{ fontWeight: 'initial' }} fontColor={color}>
              <NumberFormat
                value={p}
                displayType="text"
                thousandSeparator={true}
                fixedDecimalScale={true}
                decimalScale={2}
              />
            </LargePriceLabel>
            <LargePriceLabel style={{ fontWeight: 'initial' }} fontColor={color}>
              <NumberFormat
                value={nc}
                prefix={indicator}
                displayType="text"
                thousandSeparator={true}
                fixedDecimalScale={true}
                decimalScale={2}
              />
            </LargePriceLabel>
            <LargePriceLabel style={{ fontWeight: 'initial' }} fontColor={color}>
              <NumberFormat
                value={p !== nc ? 100 * nc / (p - nc) : 0}
                prefix={indicator}
                displayType="text"
                thousandSeparator={true}
                fixedDecimalScale={true}
                decimalScale={2}
                suffix="%"
              />
            </LargePriceLabel>
          </WatchBoxPricing>
          <WatchBoxRallyTable borderColor={active_name ? active_name === 'SELL RALLY' ? colors.accents.bad.base : colors.accents.good.base : themes[themeName].core.alternateBackground}>
          { getWatchBoxRallyItems() }
          </WatchBoxRallyTable>
          <WatchBoxDetailedTable>
            <WatchBoxDetailedItem style={{ borderRight: `solid 1px ${active_name ? active_name === 'SELL RALLY' ? colors.accents.bad.base : colors.accents.good.base : themes[themeName].core.textColor}`, borderBottom: `solid 1px ${active_name === 'SELL RALLY' ? colors.accents.bad.base : themes[themeName].core.alternateBackground}` }}>
              <LabeledText style={{ opacity: 1, color: active_name === 'SELL RALLY' ? colors.accents.bad.base : themes[themeName].core.textColor }}>Extreme Sell Prediction</LabeledText>
              <LargePriceLabel fontColor={colors.accents.bad.base}>
                <NumberFormat
                  value={sell_p_price}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              </LargePriceLabel>
            </WatchBoxDetailedItem>
            <WatchBoxDetailedItem style={{ borderBottom: `solid 1px ${active_name === 'BUY DIP' ? colors.accents.good.base : themes[themeName].core.alternateBackground}` }}>
              <LabeledText style={{ opacity: 1, color: active_name === 'BUY DIP' ? colors.accents.good.base : themes[themeName].core.textColor }}>Extreme Buy Prediction</LabeledText>
              <LargePriceLabel fontColor={colors.accents.good.base}>
              <NumberFormat
                  value={buy_p_price}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              </LargePriceLabel>
            </WatchBoxDetailedItem>
            <WatchBoxDetailedItem style={{ borderRight: `solid 1px ${themes[themeName].core.textColor}`, borderBottom: `solid 1px ${themes[themeName].core.textColor}` }}>
              <LabeledText >Target</LabeledText>
              <LargePriceLabel>
                <NumberFormat
                  value={sell_p_tgt}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              </LargePriceLabel>
            </WatchBoxDetailedItem>
            <WatchBoxDetailedItem style={{ borderBottom: `solid 1px ${themes[themeName].core.textColor}` }}>
              <LabeledText>Target</LabeledText>
              <LargePriceLabel>
                <NumberFormat
                  value={buy_p_tgt}
                  displayType="text"
                  thousandSeparator={true}
                  fixedDecimalScale={true}
                  decimalScale={2}
                />
              </LargePriceLabel>
            </WatchBoxDetailedItem>
            <WatchBoxDetailedItemTrendTrade signal={trendTradeSignal} color={TrendTradeColor.current}>
              <WatchBoxDetailedItemBordered options={['SELL', 'BUY', 'STOPOUT']} item={stmomoData ? stmomoData.stmomo : ''} color={stopoutVisibility ? 'transparent' : TrendTradeColor.current}>
                <WatchBoxLabeledPair style={{ textDecoration: 'underline' }}>
                  Trend Trade
                  {trendTradeSignal && (
                    <div onClick={handleBellClick} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon size="1x" icon={faBellSlash} />
                    </div>
                  )}
                </WatchBoxLabeledPair>
                {!stopoutVisibility && (
                  <React.Fragment>
                    <WatchBoxLabeledPair style={{ lineHeight: '1.5em' }}>
                      <div style={{ fontSize: '0.8em'}}>{getTradeStartDateTime('Date')}</div>
                    </WatchBoxLabeledPair>
                    <WatchBoxLabeledPair style={{ lineHeight: '1.5em' }}>
                      <div style={{ fontSize: '0.8em' }}>{getTradeStartDateTime('Time')}</div>
                    </WatchBoxLabeledPair>
                    {getTrend()}
                    <WatchBoxLabeledPair>
                      <div style={{ fontSize: '.7em' }}>STOP OUT</div>
                      <div style={{color: colors.accents.aware.base }}>
                        {stmomoData ?
                          stmomoData.projected_stop_out === undefined ? '' : stmomoData.projected_stop_out.toFixed(2)
                        : ''}
                      </div>
                    </WatchBoxLabeledPair>
                  </React.Fragment>
                )}
              </WatchBoxDetailedItemBordered>
            </WatchBoxDetailedItemTrendTrade>
            <WatchBoxDetailedItemNewTrend signal={AITrendSignal} color={AITrendColor} style={{ gridGap: '0'}}>
              <WatchBoxDetailedItemBordered options={['BUY', 'SELL']} item={symbol === 'ES' ? justinBuyValue : ''} color={symbol === 'ES' ? AITrendColor : ''}>
                <WatchBoxLabeledPair style={{ textDecoration: 'underline', marginBottom: '6px' }}>
                  A.I. TREND
                  {AITrendSignal && (
                    <div onClick={() => setAITrendSignal(false)} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon size="1x" icon={faBellSlash} />
                    </div>
                  )}
                </WatchBoxLabeledPair>
                <React.Fragment>
                  <WatchBoxAITrendInfo>
                    <LabeledText style={{ fontSize: '1.2rem' }}>TJ1</LabeledText>
                    {(symbol === 'ES' && justinTrend) && (
                      <React.Fragment>
                        <LabeledText style={{ color: AITrendColor, fontSize: '1.1rem', justifySelf: 'center' }}>
                          {justinBuyValue}
                        </LabeledText>
                        <LabeledText style={{ color: AITrendColor, fontSize: '1.1rem', justifySelf: 'end' }}>
                          <NumberFormat
                            value={justinTrend.v * 100}
                            displayType="text"
                            thousandSeparator={true}
                            fixedDecimalScale={true}
                            decimalScale={2}
                            suffix="%"
                          />
                        </LabeledText>
                      </React.Fragment>
                    )}
                  </WatchBoxAITrendInfo>
                </React.Fragment>
              </WatchBoxDetailedItemBordered>
            </WatchBoxDetailedItemNewTrend>
          </WatchBoxDetailedTable>
            <WatchBoxHistoryItem borderColor={themes[themeName].core.textColor}>
            <WatchBoxLabeledPair>
              <div style={{ cursor: 'pointer', fontSize: '.8rem' }} onClick={handleHistoryClick}>Trade History</div>
            </WatchBoxLabeledPair>
          </WatchBoxHistoryItem>
        </React.Fragment>
        );
      }}
    </ThemeConsumer>
  );
};


const blink = (color: string) => keyframes`
  0% { background-color: ${color}; }
  50% { background-color: transparent; }
  100% { background-color: ${color}; }
`;

const WatchBoxPricing = styled(DataContainer)`
display: grid;
grid-template-columns: auto 1fr auto;
justify-items: center;
`;

const WatchBoxRallyItem = styled(DataContainer)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  align-items: center;
  border-radius: 3px;
  padding: 9px;
  margin: 4px;
  background-color: ${({ theme }) => theme.core.alternateBackground};
`;

const WatchBoxDetailedTable = styled(DataContainer)`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const WatchBoxDetailedItem = styled(DataContainer)`
  display: grid;
  grid-auto-flow: row;
  padding: 10px;
  align-items: start;
  grid-gap: 0.5em;
`;

const WatchBoxDetailedItemTrendTrade = styled(WatchBoxDetailedItem) <{ signal: boolean, color: string }>`
  border-right: 1px solid white;
  padding: 0;
  ${({ signal, color }) => signal
    ? css`animation: ${blink(color)} .5s linear infinite;`
    : css`background: none;`
  }
`;

const WatchBoxDetailedItemNewTrend = styled(WatchBoxDetailedItem) <{ signal: boolean, color: string }>`
  padding: 0;
  ${({ signal, color }) => signal
    ? css`animation: ${blink(color)} .5s linear infinite;`
    : css`background: none;`
  }
`;

const WatchBoxDetailedItemBordered = styled.div<{ options: string[], item: string, color: string }>`
  padding: 6px;
  height: 100%;
  ${({ options, item, color }) => options.includes(item)
    ? css`border: 4px solid ${color};`
    : css`border: 4px solid transparent;`
  }
`

const WatchBoxHistoryItem = styled(DataContainer)<{borderColor: string}>`
  display: grid;
  grid-auto-flow: row;
  padding: 10px;
  justify-content: center;
  border-top: 1px solid ${({ borderColor }) => borderColor};
`;

const WatchBoxRallyTable = styled(DataContainer)<{borderColor: string}>`
  border-radius: 3px;
  border: 3px solid ${({ borderColor }) => borderColor};
  padding: 4px;
`;

const LargePriceLabel = styled(NormalText)<{fontColor?: string}>`
  font-size: 1.5em;
  font-weight: 300;
  color: ${({ fontColor, theme }) => fontColor || theme.core.textColor};
`;

const WatchBoxLabeledPair = styled(DataContainer)<{color?: string}>`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: space-between;
  color: ${({theme, color}) => color || theme.core.textColor};
  padding-bottom: 6px;
`;

const WatchBoxAITrendInfo = styled(DataContainer) <{ color?: string }>`
  align-items: center;
  display: grid;
  grid-template-columns: 25% 35% auto;
  margin: 10px 0;
  color: ${({ theme, color }) => color || theme.core.textColor};
  `;

const LabeledText = styled.div<{fontColor?: string}>`
  color: ${({ theme, fontColor }) => (fontColor || theme.core.textColor)};
  font-family: 'Lato';
  font-size: 0.8rem;
`;
